// src/lib/xlsx.ts
// 2026-05-22: 集計データダウンロードを CSV → xlsx に切替 (ユーザ要望)
// 2026-05-28: SheetJS (xlsx) → ExcelJS に移行 (pentest 残懸念対応、
//   xlsx 0.18.5 の Prototype Pollution + ReDoS 解消)。 dynamic import で
//   bundle size 影響緩和 (= export ボタン押下時のみ load)。

// pentest 対応 P1: Excel formula injection 対策。 DB 由来文字列が
// =, +, -, @, |, タブ, 改行 で始まる場合、 Excel が式として解釈 → 職員端末で
// 外部通信や情報漏洩を誘導されるリスク。 ' prefix で literal 文字列扱いに無害化。
function sanitizeForExcel(v: unknown): unknown {
  if (typeof v !== "string" || v.length === 0) return v;
  if (/^[=+\-@|\t\r\n]/.test(v)) return "'" + v;
  return v;
}
function sanitizeRow(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(row)) {
    out[k] = sanitizeForExcel(row[k]);
  }
  return out;
}

// ── 複数シート + 書式付き出力 (2026-07-13: 科別/月別マトリックスの整形出力用) ──
//   各シート: ヘッダ太字・1行目固定・列幅自動 (全角=2/半角=1 換算、8〜40)。
export interface XlsxSheet {
  name: string;
  rows: Record<string, unknown>[];
  columns?: string[];
}
function dispWidth(s: string): number {
  let w = 0;
  for (const ch of s) w += /[^\x00-\x7F]/.test(ch) ? 2 : 1;
  return w;
}
export async function downloadXlsxSheets(sheets: XlsxSheet[], filename: string): Promise<void> {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  for (const sh of sheets) {
    const safeRows = sh.rows.map(sanitizeRow);
    const cols = sh.columns ?? (safeRows.length > 0 ? Object.keys(safeRows[0]) : []);
    const ws = wb.addWorksheet(sh.name);
    ws.columns = cols.map((c) => ({ header: c, key: c }));
    for (const row of safeRows) ws.addRow(row);
    ws.getRow(1).font = { bold: true };
    ws.views = [{ state: "frozen", ySplit: 1 }];
    ws.columns.forEach((col, i) => {
      const key = cols[i];
      let w = dispWidth(key);
      for (const r of safeRows) {
        const v = r[key];
        if (v != null) w = Math.max(w, dispWidth(typeof v === "number" ? v.toLocaleString("ja-JP") : String(v)));
      }
      col.width = Math.min(Math.max(w + 2, 8), 40);
    });
  }
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const out = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = out;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadXlsx(
  rows: Record<string, unknown>[],
  filename: string,
  columns?: string[],
  sheetName: string = "Sheet1",
): Promise<void> {
  // dynamic import (= bundle に直接含めず、 export ボタン押下時に load)
  const ExcelJS = (await import("exceljs")).default;
  const safeRows = rows.map(sanitizeRow);
  const cols = columns ?? (safeRows.length > 0 ? Object.keys(safeRows[0]) : []);

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(sheetName);
  // 列定義 (= header + key 同一)
  ws.columns = cols.map((c) => ({ header: c, key: c }));
  // データ追加
  for (const row of safeRows) {
    ws.addRow(row);
  }

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const out = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = out;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
